/* Gallery.jsx — PPR photo gallery.
   Photos are read from /img/manifest.json at runtime. Regenerate the manifest
   after dropping new photos into /img by running tools/build-gallery-manifest.js.
   Each manifest item carries a `cat` that has to match an id in `cats` below,
   or the photo only ever shows under "All photos". */

// CR #195: show 25 photos initially with a "See more" toggle to load the rest.
const GALLERY_PREVIEW_COUNT = 25;

// A manifest item's `cat` must match an id here or the photo only ever shows
// under "All photos".
/* Order is J.J.'s, and it is roughly newest-and-biggest first rather than
   chronological: the Intro Camps are the largest set and the one families are
   most often sent here to find, and the Spring Showcase is the oldest.
   "All photos" stays at the head as the reset -- it is the state the page
   loads in, not one of the four sets being ordered.

   No year in any label. The sets recur every season, and a chip that reads
   "2026" has to be edited the moment 2027 photos land or it starts lying;
   the heroes still date each set in words, which is where a year belongs. */
const GALLERY_CATS = [
  { id: "all", label: "All photos" },
  { id: "introcamps", label: "Intro Camps" },
  // The ONE chip that is not a `cat` on any manifest item. Everything shot on
  // finale night carries cat:"introcamps" and its rec center's `loc`, which is
  // what keeps each clip and still in "All camps" AND in its own camp's grid
  // beside that camp's summer photos. This chip is the third way to reach the
  // same items -- the whole night, on its own -- and it filters on `dell`
  // instead. See VIRTUAL_CATS.
  { id: "dell", label: "Dell Finale" },
  // The set is the CAMP, not the show -- YPTC staged Wonderland in 2026 and
  // will stage something else next year, so the chip is named for the thing
  // that stays. "Wonderland" is still on the sub-row and all over the hero, so
  // nobody arrives here looking for the show and fails to find it.
  { id: "wonderland", label: "YPTC" },
  { id: "firesafety", label: "Fire Prevention Plays" },
  { id: "showcase", label: "Spring Showcase" },
];

// Two sets are big enough to want a second chip row, and they slice on
// different things -- Intro Camps by rec center, Wonderland by night. Both ride
// the same `loc` field on a manifest item and the same ?loc= query, because to
// the filtering code "which sub-chip does this photo belong to" is one
// question. A set with no entry in SUBFILTERS ignores `loc` entirely, and the
// row renders only while that set's chip is active, so the top row never grows.
// A photo whose `loc` matches nothing here only ever shows under the "All" chip.
const CAMP_LOCATIONS = [
  { id: "all", label: "All camps" },
  { id: "athletic", label: "Athletic" },
  { id: "holmesburg", label: "Holmesburg" },
  { id: "hawthorne", label: "Hawthorne" },
  { id: "kingsessing", label: "Kingsessing" },
  { id: "kendrick", label: "Kendrick" },
];

// Wonderland runs rehearsals then two performance nights. Night 1 and Night 2
// are listed before their photos exist on purpose: same rule the camps row
// follows, where hiding an empty chip would make the run look shorter than it
// was. Drop files as yptc-wonderland-night1-*.jpg and re-run
// tools/build-gallery-manifest.js and they land here with no code change.
const WONDERLAND_SETS = [
  { id: "all", label: "All Wonderland" },
  { id: "rehearsal", label: "Rehearsal" },
  { id: "night1", label: "Night 1" },
  { id: "night2", label: "Night 2" },
];

// cat id -> the second row it gets. `label` is the row's own prefix, which is
// what keeps "Rehearsal" from reading as a sixth rec center.
const SUBFILTERS = {
  introcamps: { label: "Rec center", options: CAMP_LOCATIONS },
  // The camps row is prefixed by what its chips ARE (rec centers). This row's
  // chips are all one venue, so the prefix names it -- the row reads "Venice
  // Island: rehearsal, night 1, night 2" rather than repeating the venue on
  // every chip.
  wonderland: { label: "Venice Island", options: WONDERLAND_SETS },
  // Same list of rec centers as the camps row, doing a different job: that row
  // answers "which camp", this one answers "which camp, on finale night". The
  // options here are a FALLBACK -- GalleryPage rebuilds this row from the
  // manifest so a camp with nothing from that night gets no chip, and the
  // event-wide footage that belongs to no camp is not filed under one.
  dell: { label: "On the night", options: CAMP_LOCATIONS },
};

const subOptions = (cat) => (SUBFILTERS[cat] || {}).options || [];

// Chips that no item's `cat` will ever equal. They filter on something else,
// so they must be kept out of every place that treats GALLERY_CATS as the list
// of real sets: the round-robin that fills the first screen, and the "how many
// collections" stat.
const VIRTUAL_CATS = new Set(["dell"]);

const isClip = (i) => i.kind === "video";

// Star Players is its own little album on this page, separate from the manifest
// grid. Hoisted out of the JSX so the lightbox can be handed the whole list and
// arrow through it -- an inline literal rebuilds on every render, which makes
// "which photo am I on" unanswerable.
const STAR_PLAYERS = [
  { src: "/img/star-players-cast.jpg",   alt: "Star Players cast on stage" },
  { src: "/img/star-players-dance.jpg",  alt: "Star Players dance number in performance" },
  { src: "/img/star-players-awards.jpg", alt: "Star Players annual awards dinner" },
  { src: "/img/star-players-screen.jpg", alt: "Star Players projection backdrop during a show" },
  { src: "/img/star-players-bazaar.jpg", alt: "Star Players choir performing at the community bazaar" },
  { src: "/img/star-players-santa.jpg",  alt: "Star Players choir at Breakfast with Santa" },
  { src: "/img/star-players-step.jpg",   alt: "Star Players performing Step in Time" },
];

// What the lightbox's Download button hands over.
//
// THE FALLBACK IS THE POINT. `hq` is stamped by the manifest builder for photos
// that have a print-size copy in img/hq/ (2400px, made from the photographer's
// originals by tools/build-hq.mjs). Most do not, and some never can -- the
// Athletic camp candids were delivered to us already at 1600px, so no better
// file exists to make. A photo with no copy still downloads; it just hands over
// the same web file the page is showing. That fallback is unchanged and `full`
// below still reports which one a given photo got.
//
// THE LABEL IS DELIBERATELY THE SAME ON EVERY PHOTO (2026-08-18, J.J.'s call,
// made with the tradeoff in front of him). It used to vary -- "Download full
// size" where a print copy existed, plain "Download" where it did not -- so the
// wording could not promise what a particular file was not. J.J. wanted the
// buttons uniform and chose this over the alternative of dropping the phrase
// everywhere.
//
// WHAT THAT COSTS, WRITTEN DOWN SO IT IS NOT REDISCOVERED AS A BUG: 350 of the
// 1442 photos have no print copy, and on those this button now says "full size"
// over a 1600px file, which is about a 5x3 print. That is the wording parents
// complained about in #207, which is the reason the split label existed at all.
// 134 of the 350 (Athletic) can never be fixed -- they were delivered to us at
// 1600px. So this does not become true over time on its own.
//
// DO NOT "FIX" THIS BACK TO A CONDITIONAL WITHOUT ASKING J.J. It reads exactly
// like the regression it is not.
function downloadFor(item) {
  const file = String(item.src || "").replace(/^\/img\//, "");
  return {
    href: item.hq ? `/img/hq/${file}` : item.src,
    name: file,
    full: !!item.hq,
    label: "Download full size",
  };
}

// Each camp closes its session with a full production, filmed end to end. The
// key is a CAMP_LOCATIONS id; a location with no entry here renders no video
// section at all rather than an empty player -- Athletic has no recording yet.
// `show` is the production that camp actually staged, and it is the headline,
// so it has to match the recording. Source filenames are NOT authoritative on
// spelling: the Holmesburg file is named "Holmesberg". The site says Holmesburg
// everywhere and that wins, same rule the Fire Prevention Play set follows.
// These are Drive file ids, not the folder ids J.J. sent -- a folder cannot be
// embedded. Re-derive one for a new camp with:
//   curl -sL "https://drive.google.com/embeddedfolderview?id=<FOLDER_ID>#list" \
//     | grep -oE '/file/d/[A-Za-z0-9_-]+'
// and confirm the file reads anonymously before shipping it (a thumbnail fetch
// with no cookies returning a real image is the cheap check), because a file
// that is private to the uploader renders as a sign-in box on a public site.
const CAMP_VIDEOS = {
  hawthorne:   { id: "1UV8OKMig0Z1Oh1wzz3aXJBpeZHTYdntM", show: "Philadelphia 250" },
  holmesburg:  { id: "1DrO7aF6NzMnMaOdZNYqQ_1cX1-7L15M4", show: "Annie" },
  kendrick:    { id: "1pygePkP1YLTlENlfCMnZlphw3j5Ianxr", show: "Shrek" },
  kingsessing: { id: "1U0sdwNw4tikrhzcQ156dcOhKtAY-l_RJ", show: "Joseph" },
};

// YPTC's two performance nights of Wonderland at Venice Island, 6 and 7 August
// 2026. Keyed by the WONDERLAND_SETS id so the night chips and this card agree
// on which night is which: Thursday the 6th is Night 1, Friday the 7th is
// Night 2. Same rule as CAMP_VIDEOS -- Drive FILE ids, not the folder id. They
// are the two full-show files ("Wonderland Thursday.m4v", "Wonderland
// Friday.m4v") in the "Wonderland YPTC" folder J.J. sent 2026-10-08, and both
// thumbnails were confirmed to read anonymously before shipping. The folder
// also holds each night cut into two acts; the card plays the full-show files.
const WONDERLAND_VIDEOS = {
  night1: { id: "10wN81DW8wcI_Zhkgg0_oQu5L6-N6uiHh", show: "Wonderland", when: "Thursday, August 6", chip: "Night 1" },
  night2: { id: "1Yys-9K2e7SfOkXNycRK00jykxxLwDW2w", show: "Wonderland", when: "Friday, August 7", chip: "Night 2" },
};

// Deep link: /#gallery?set=wonderland opens straight into one set, which is how
// the home-page ticket popup links to the behind-the-scenes photos. Same
// hash-with-query shape the shop-success route already uses (CR #264): App's
// router splits the route off the "?" and leaves the query alone, and only
// rewrites the hash when the ROUTE part changed, so the query survives.
// Unknown or missing set falls back to "all" rather than an empty grid.
function catFromHash() {
  const q = (window.location.hash || "").split("?")[1];
  const v = q ? new URLSearchParams(q).get("set") : null;
  return GALLERY_CATS.some(c => c.id === v) ? v : "all";
}

// Second half of the same deep link: /#gallery?set=introcamps&loc=holmesburg,
// or /#gallery?set=wonderland&loc=night1. Validated against the SET's OWN
// options, not against every id in the file -- ?set=introcamps&loc=night1 has
// to fall back to "all camps" rather than filter the camps grid down to an
// empty page.
function locFromHash(cat) {
  const q = (window.location.hash || "").split("?")[1];
  const v = q ? new URLSearchParams(q).get("loc") : null;
  return subOptions(cat).some(c => c.id === v) ? v : "all";
}

function GalleryPage() {
  const [items, setItems] = React.useState([]);
  const [filter, setFilter] = React.useState(catFromHash);
  const [loc, setLoc] = React.useState(() => locFromHash(catFromHash()));
  const [lightbox, setLightbox] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [expanded, setExpanded] = React.useState(false);

  // Every photo in this gallery lives at the same route, so the page-view
  // tracker cannot tell one apart from another. Opening the lightbox is the
  // event worth counting -- a thumbnail scrolling past is not a view -- so it
  // is the one place that reports. Nothing here can throw into the click
  // handler: pprTrackMedia swallows its own errors and is simply absent when
  // analytics.js has not loaded. See trackMedia in analytics.js.
  // THE LIGHTBOX HOLDS A POSITION IN AN ALBUM, NOT A PHOTO. It used to hold the
  // item alone, which is all a single view needs and nothing an arrow key can
  // work with. `album` is whichever list was clicked -- the filtered grid, or
  // Star Players' own six -- so stepping never wanders out of the set the
  // visitor thinks they are in.
  // The album is the WHOLE filtered set, not the 25 the grid is showing.
  // "Show all" is a grid-density control, not the edge of the album, and
  // stopping the arrows at 25 of 68 would send a visitor back out to press it
  // and start over.
  const openLightbox = (item, collection, album) => {
    const list = album && album.length ? album : [item];
    const index = Math.max(0, list.findIndex(i => i.src === item.src));
    setLightbox({ list, index, collection });
    trackMedia(list[index], collection);
  };

  // Arrowing to a photo is an EXPLICIT open, so it counts, exactly as clicking
  // a thumbnail does. (Scrolling past one still does not -- see the media_views
  // note in CLAUDE.md.) pprTrackMedia dedupes per media id per session, so
  // arrowing back and forth over the same five photos does not inflate anything.
  const trackMedia = (item, collection) => {
    if (!item || !window.pprTrackMedia) return;
    window.pprTrackMedia({
      id: item.src,
      kind: isClip(item) ? "video" : "photo",
      title: item.caption || item.alt,
      collection: collection || item.cat || filter,
    });
  };

  // Wraps at both ends. A visitor at the last photo of a camp pressing right
  // wants the first one back, not a dead key with no feedback.
  const stepLightbox = React.useCallback((delta) => {
    setLightbox(lb => {
      if (!lb || lb.list.length < 2) return lb;
      const index = (lb.index + delta + lb.list.length) % lb.list.length;
      trackMedia(lb.list[index], lb.collection);
      return { ...lb, index };
    });
  }, []);

  // Keys are bound only while the overlay is open, and the listener is torn
  // down with it -- otherwise arrow keys would keep firing at a closed
  // lightbox and Escape would fight every other dialog on the page.
  React.useEffect(() => {
    if (!lightbox) return undefined;
    const onKey = (e) => {
      if (e.key === "ArrowRight") { e.preventDefault(); stepLightbox(1); }
      else if (e.key === "ArrowLeft") { e.preventDefault(); stepLightbox(-1); }
      else if (e.key === "Escape") setLightbox(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightbox, stepLightbox]);

  React.useEffect(() => {
    let cancelled = false;
    // cache:"no-cache" forces a revalidation on every load instead of trusting
    // the stored copy. It is not belt-and-braces with the vercel.json header --
    // it is the only half that can fix a browser TODAY. /img/(.*) used to serve
    // this file `immutable`, so any visitor who opened the gallery before
    // 2026-08-05 has a year-long entry a browser will not recheck on its own,
    // and the new Wonderland chip read that stale manifest as an empty set.
    // Correcting the header only helps caches that have not been poisoned yet.
    // Cheap: a 304 with no body whenever the manifest has not changed.
    fetch("/img/manifest.json", { cache: "no-cache" })
      .then(r => r.ok ? r.json() : Promise.reject(new Error(`status_${r.status}`)))
      .then(j => { if (!cancelled) setItems(j.items || []); })
      .catch(e => { if (!cancelled) setError(String(e.message || e)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  // Someone already sitting on /#gallery who clicks a ?set= link does not
  // remount this component, so the initial state above never re-runs. Without
  // this the popup's behind-the-scenes link would look dead to exactly the
  // people already browsing photos.
  React.useEffect(() => {
    const onHash = () => {
      const cat = catFromHash();
      setFilter(cat);
      setLoc(locFromHash(cat));
      setExpanded(false);
    };
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);

  // Clicking a chip makes the URL shareable. replaceState (not a hash write)
  // keeps every chip click out of the back button AND does not fire
  // hashchange, so this cannot loop against the listener above.
  // Both chip rows write through here so there is one place that builds the
  // query. Leaving one set's chip and coming back should not strand you on a
  // rec center you picked ten minutes ago, so switching SET always resets the
  // location -- only a sub-chip click carries a location through.
  const selectView = React.useCallback((id, nextLoc) => {
    setFilter(id);
    setLoc(nextLoc);
    setExpanded(false);
    const base = (window.location.hash || "#gallery").split("?")[0] || "#gallery";
    const q = new URLSearchParams();
    if (id !== "all") q.set("set", id);
    // Only written for a set that HAS a second row, so a stale ?loc= cannot be
    // built for a set that would ignore it.
    if (SUBFILTERS[id] && nextLoc !== "all") q.set("loc", nextLoc);
    const s = q.toString();
    window.history.replaceState(null, "", s ? `${base}?${s}` : base);
  }, []);

  const selectCat = React.useCallback((id) => selectView(id, "all"), [selectView]);
  const selectLoc = React.useCallback((id) => selectView(filter, id), [selectView, filter]);

  // "All photos" is the landing state, so what sits at the top of that grid is
  // the first thing a family sees. Manifest order is filesystem order, which
  // put the oldest set (the April showcase) first and nothing else.
  //
  // Two different jobs, hence two passes:
  //   the first screen   one photo at a time from each set in chip order, so
  //                      the 25 above the "See more" fold advertise all four
  //                      sets rather than 25 Intro Camp shots (there are 280
  //                      of those against 50 Wonderland -- straight set order
  //                      buries three sets below the fold entirely)
  //   everything after   grouped by set, in chip order, because past the fold
  //                      someone is browsing and a set should stay together
  //
  // Both passes preserve manifest order WITHIN a set, so every filtered view
  // is byte-for-byte what it was. A photo whose `cat` is not a known chip
  // takes no round-robin turn and lands at the end.
  const CAT_RANK = GALLERY_CATS.filter(c => c.id !== "all" && !VIRTUAL_CATS.has(c.id)).map(c => c.id);
  const ordered = React.useMemo(() => {
    const bySet = CAT_RANK.map(id => items.filter(i => i.cat === id));
    const rest = items.filter(i => !CAT_RANK.includes(i.cat));
    const lead = [];
    // Rounds, not slices: a set that runs out just stops taking turns, so a
    // set with three photos cannot hold a slot open in every later round.
    for (let n = 0; lead.length < GALLERY_PREVIEW_COUNT; n++) {
      if (!bySet.some(g => g.length > n)) break;
      bySet.forEach(g => { if (g.length > n && lead.length < GALLERY_PREVIEW_COUNT) lead.push(g[n]); });
    }
    const taken = new Set(lead);
    return [...lead, ...bySet.flat().filter(i => !taken.has(i)), ...rest];
  }, [items]);

  // Spread heights/widths in a deterministic way so masonry doesn't reshuffle.
  // A clip takes a fixed 2x2 instead of a turn in the pattern: two columns by
  // two 180px rows is the only cell in this grid that is roughly 16:9, and a
  // drone shot cropped to a 1x1 square throws away the width that is the whole
  // reason it was shot from the air.
  const sized = React.useMemo(() => ordered.map((it, i) => {
    if (isClip(it)) return { ...it, w: 2, h: 2 };
    const pattern = [
      { w: 2, h: 1 }, { w: 1, h: 1 }, { w: 1, h: 1 }, { w: 1, h: 2 },
      { w: 2, h: 1 }, { w: 1, h: 1 }, { w: 1, h: 1 }, { w: 2, h: 1 },
      { w: 1, h: 1 }, { w: 1, h: 2 }, { w: 1, h: 1 }, { w: 2, h: 1 }
    ];
    const p = pattern[i % pattern.length];
    return { ...it, w: p.w, h: p.h };
  }), [ordered]);

  const filtered = React.useMemo(() => {
    if (filter === "all") return sized;
    // Dell Finale gathers everything shot on finale night -- clips AND stills --
    // across whatever set they belong to, so it asks about `dell` and never
    // about `cat`. Its own second row then narrows the night to one camp, and
    // the event-wide footage drops out of that narrowing exactly as it should:
    // it carries no `loc` because no single camp owns it.
    // The finale night is its own tab and ONLY its own tab. It used to lead the
    // camps grids too, on the reasoning that a camp's finale belongs with that
    // camp -- J.J.'s call after seeing it is that the camps tab should be the
    // summer at the rec center, full stop, and the Dell has its own chip two
    // along. The items still carry their rec center; that is what Dell Finale's
    // own second row narrows on, and what the camp heroes link out with.
    const inCat = filter === "dell"
      ? sized.filter(i => i.dell)
      : sized.filter(i => i.cat === filter && !i.dell);
    // Only a set with a second row narrows twice. A photo with no `loc` still
    // shows under that row's "All" chip rather than falling out of the gallery.
    if (!SUBFILTERS[filter] || loc === "all") return inCat;
    return inCat.filter(i => i.loc === loc);
  }, [sized, filter, loc]);

  // Every count below is PHOTOS ONLY. A stat labelled "Photos" that quietly
  // includes six drone clips is wrong in the one place a visitor can check it
  // against the grid. Clips get their own count and their own label.
  const photos = React.useMemo(() => items.filter(i => !isClip(i)), [items]);

  // The finale night, as its own slice: clips and stills together. `dellCount`
  // is what the chip's existence hangs on, so a season with stills and no
  // footage still gets the chip.
  const dellItems = React.useMemo(() => items.filter(i => i.dell), [items]);
  const dellCount = dellItems.length;

  // Dell Finale is the only chip anywhere that hides itself. Every other one
  // stands for something we have committed to publishing, so an empty chip
  // still reads as "coming" -- that is why the two Wonderland nights and the
  // camps with no photos yet all stay clickable. Drone footage is a one-off
  // J.J. may or may not have in a given season, so an empty grid behind it
  // would just be a dead chip. It appears when the manifest lands.
  const cats = React.useMemo(
    () => GALLERY_CATS.filter(c => c.id !== "dell" || dellCount > 0),
    [dellCount]
  );

  // Dell Finale's own second row, built from what is actually in the manifest
  // rather than from CAMP_LOCATIONS: a camp with nothing from that night has
  // no chip, so the row can never offer an empty view of itself. Order follows
  // CAMP_LOCATIONS so it reads the same as the camps row. Every other set uses
  // its static options.
  const subChips = React.useMemo(() => {
    if (filter !== "dell") return subOptions(filter);
    const present = new Set(dellItems.map(i => i.loc).filter(Boolean));
    return CAMP_LOCATIONS
      .filter(c => c.id === "all" || present.has(c.id))
      // Plain "All" rather than the camps row's "All camps": this row's reset
      // also holds the wides that belong to no camp, and those are the first
      // thing that falls out the moment you pick one.
      .map(c => (c.id === "all" ? { id: "all", label: "All" } : c));
  }, [filter, dellItems]);

  // Set counts exclude the finale night for the same reason the grids do: those
  // stills are on their own tab now, and a "Photos" number that counts material
  // the visitor cannot see in that view is the one lie a stat block can tell.
  const setPhotos = React.useMemo(() => photos.filter(i => !i.dell), [photos]);

  const countOf = React.useMemo(() => {
    const c = {};
    setPhotos.forEach(i => { c[i.cat] = (c[i.cat] || 0) + 1; });
    return c;
  }, [setPhotos]);

  // Per-sub-chip counts, keyed "cat/loc", so a narrowed hero can state its own
  // number and a sub-chip with no photos yet reads "—" instead of borrowing the
  // set total. Keyed by both halves because "night1" and a rec center id live
  // in the same field and only the cat tells them apart.
  const subCountOf = React.useMemo(() => {
    const c = {};
    setPhotos.forEach(i => { if (i.loc) { const k = `${i.cat}/${i.loc}`; c[k] = (c[k] || 0) + 1; } });
    return c;
  }, [setPhotos]);

  // Same shape, over the finale night. A camp hero uses it to decide whether to
  // point at the Dell tab at all -- a camp that was never filmed gets no link
  // rather than one to an empty view.
  const dellCountOf = React.useMemo(() => {
    const c = {};
    dellItems.forEach(i => { if (i.loc) { c[i.loc] = (c[i.loc] || 0) + 1; } });
    return c;
  }, [dellItems]);

  // Counted from the manifest, not hardcoded, so the summary hero stays true
  // when a set is added or an empty one is retired.
  const collectionCount = Object.keys(countOf).length;

  // The hero follows the active filter, because one headline cannot be right for
  // every view. "All photos" summarises the archive; each set gets its own
  // framing, so a grid of camp rehearsals never sits under a "Spring Arts
  // Showcase" headline. Every hero counts only what it is describing --
  // items.length under the showcase hero would credit it with the camp photos.
  const HEROES = {
    all: {
      // The archive hero is the ONE that must not name a venue. This set spans
      // the Free Library fireplay final, the Intro Camps at five rec centers
      // and the Dell showcase, so "Venice Island PAC" was crediting one venue
      // for citywide work. Each individual set eyebrow still names its own
      // venue, which is where that belongs.
      eyebrow: "2026 season · Philadelphia Parks & Recreation",
      title: <>The 2026<br />Season.</>,
      blurb: <>Everything we photographed this year &mdash; April&rsquo;s citywide
        Spring Arts Showcase, Young Performers Theatre Camp building
        <em> Wonderland</em> across the summer, July&rsquo;s citywide Fire
        Prevention Play competition, and the Intro Camps at rec centers across
        the city. Pick a set below to see just that one.</>,
      stats: [
        { n: photos.length || "—", label: "Photos" },
        { n: collectionCount || "—", label: "Collections" },
        { n: "2026", label: "Season" },
      ],
    },
    showcase: {
      eyebrow: "04 · 28 · 2026 · Venice Island PAC",
      title: <>Spring Arts<br />Showcase.</>,
      blurb: <>One night, 12 rec centers, 100+ young performers. Highlights from
        our spring 2026 showcase at Venice Island Performing Arts Center.</>,
      stats: [
        { n: countOf.showcase || "—", label: "Photos" },
        { n: "100+", label: "Performers" },
        { n: "24", label: "Acts" },
        { n: "12", label: "Rec centers" },
      ],
    },
    wonderland: {
      eyebrow: "Summer 2026 · Venice Island PAC",
      title: <>Making<br />Wonderland.</>,
      // Covers the whole set, which is no longer only rehearsals -- both
      // performance nights are in it now. The rehearsal sub-view keeps the
      // narrower copy this blurb used to carry; see wonderlandHero.
      blurb: <>Summer 2026 &mdash; Young Performers Theatre Camp building its
        production at Venice Island Performing Arts Center and then playing it.
        Blocking and choreography in July, the dress run in August, and both
        nights in front of an audience.</>,
      stats: [
        { n: countOf.wonderland || "—", label: "Photos" },
        { n: "12–18", label: "Ages" },
        { n: "6", label: "Week session" },
        { n: "2", label: "Show nights" },
      ],
    },
    // The one set NOT shot at Venice Island -- the citywide Fire Prevention
    // Play final is at the Free Library, so the eyebrow names that venue
    // instead. Numbers here match /#fireplay: 12 districts, one winner each.
    firesafety: {
      eyebrow: "07 · 29 · 2026 · The Free Library",
      title: <>Fire Prevention<br />Plays.</>,
      blurb: <>The citywide competition &mdash; 12 district-winning plays
        performed at the Free Library on July 29, presented by the
        Philadelphia Fire Department and Philadelphia Parks &amp; Recreation.
        Results and every play on video live on the <a href="#fireplay"
        style={{ color: "var(--c-accent)", textDecoration: "underline" }}>Fire
        Prevention Play page</a>.</>,
      stats: [
        { n: countOf.firesafety || "—", label: "Photos" },
        { n: "12", label: "Districts" },
        { n: "12", label: "Plays" },
        { n: "2026", label: "Season" },
      ],
    },
    // The Intro Camps set is the only one that is a PROGRAM run at five rec
    // centers rather than a single event, so its hero is the one that has to
    // survive being narrowed. This is the "All camps" copy; picking a location
    // rewrites the title and stats below rather than adding five more entries
    // here.
    introcamps: {
      eyebrow: "Summer 2026 · Philadelphia Parks & Recreation",
      title: <>2026 Intro<br />Camps.</>,
      blurb: <>Our summer introduction to performing arts, run at rec centers
        across the city. Campers spend the session learning the basics of
        acting, singing and movement, then put it in front of a real audience.
        Pick a rec center below to see just that camp, or see the whole season
        end on one stage at the <a href="#gallery?set=dell"
        style={{ color: "var(--c-accent)", textDecoration: "underline" }}>Dell
        Finale</a>.</>,
      stats: [
        { n: countOf.introcamps || "—", label: "Photos" },
        { n: CAMP_LOCATIONS.length - 1, label: "Rec centers" },
        { n: "2026", label: "Season" },
      ],
    },
  };
  // A location view keeps the set's framing and swaps in what actually changes.
  // Two of the five rec centers have no photos yet, and the hero has to be
  // honest about that -- it sits directly above the "no photos in this set yet"
  // empty state, so a confident stat block there would read as a broken grid
  // rather than a camp we have not published.
  // The Dell Finale chip sits in the rec center row but is not a rec center,
  // so it cannot take campHero's framing -- "Dell Finale Intro Camp." names a
  // camp that does not exist. It gets the night's own hero instead.
  function dellHero(siteId) {
    const label = (CAMP_LOCATIONS.find(c => c.id === siteId) || {}).label || "";
    const mine = siteId === "all" ? dellItems : dellItems.filter(i => i.loc === siteId);
    const nClips = mine.filter(isClip).length;
    const nPhotos = mine.length - nClips;
    const stats = [
      ...(nClips ? [{ n: nClips, label: nClips === 1 ? "Clip" : "Clips" }] : []),
      ...(nPhotos ? [{ n: nPhotos, label: nPhotos === 1 ? "Photo" : "Photos" }] : []),
      { n: "Dell Music Center", label: "Venue" },
      { n: "2026", label: "Season" },
    ];
    if (siteId === "all") {
      return {
        eyebrow: "08 · 10 · 2026 · Dell Music Center",
        title: <>The Dell<br />Finale.</>,
        blurb: <>The 2026 Summer Camp Showcase from the air and from the house
          &mdash; the citywide night that closes out the Intro Camp season, with
          every rec center on one stage. Narrow it to one camp below, or open <a href="#gallery?set=introcamps"
          style={{ color: "var(--c-accent)", textDecoration: "underline" }}>Intro
          Camps</a> to see a camp&rsquo;s whole summer.</>,
        stats,
      };
    }
    return {
      eyebrow: `08 · 10 · 2026 · ${label} at the Dell`,
      title: <>{label}<br />at the Dell.</>,
      blurb: <>{label} Recreation Center&rsquo;s camp on the Summer Camp Showcase
        stage, shot from the air and from the house. Their rehearsals and their
        summer at the rec center are on the <a href={`#gallery?set=introcamps&loc=${siteId}`}
        style={{ color: "var(--c-accent)", textDecoration: "underline" }}>{label}</a> tab.</>,
      stats,
    };
  }

  function campHero(id) {
    const label = (CAMP_LOCATIONS.find(c => c.id === id) || {}).label || "";
    const n = subCountOf[`introcamps/${id}`] || 0;
    // The finale material is no longer in this grid, so this is a pointer, not
    // a count: a camp that was filmed at the Dell gets a sentence sending them
    // to that camp's own view of the night, and a camp that was not gets
    // nothing rather than a link to an empty page.
    const atDell = dellCountOf[id] || 0;
    const dellLink = atDell ? (
      <> Their night at the Dell Music Center is on the
        {" "}<a href={`#gallery?set=dell&loc=${id}`}
        style={{ color: "var(--c-accent)", textDecoration: "underline" }}>Dell
        Finale</a> tab.</>
    ) : null;
    return {
      eyebrow: `Summer 2026 · ${label} Recreation Center`,
      title: <>{label}<br />Intro Camp.</>,
      blurb: n
        ? <>Photos from the 2026 Intro Camp at {label} Recreation Center &mdash;
          one of five sites running our summer introduction to performing arts.
          Every camp is on the <a href="#gallery?set=introcamps"
          style={{ color: "var(--c-accent)", textDecoration: "underline" }}>Intro
          Camps</a> tab.{dellLink}</>
        : <>Photos from {label} Recreation Center&rsquo;s 2026 Intro Camp are
          still being sorted and will be posted here. In the meantime, the other
          camps are on the <a href="#gallery?set=introcamps"
          style={{ color: "var(--c-accent)", textDecoration: "underline" }}>Intro
          Camps</a> tab.{dellLink}</>,
      stats: [
        { n: n || "—", label: "Photos" },
        { n: label, label: "Rec center" },
        { n: "2026", label: "Season" },
      ],
    };
  }

  // Same rule as campHero, for the other set with a second row. The set-level
  // Wonderland hero opens "Rehearsal shots from summer 2026", which stops being
  // true the moment someone picks a performance night, so a night gets its own
  // framing rather than sitting under copy that describes a different shoot.
  function wonderlandHero(id) {
    const label = (WONDERLAND_SETS.find(c => c.id === id) || {}).label || "";
    const n = subCountOf[`wonderland/${id}`] || 0;
    if (id === "rehearsal") {
      return {
        ...HEROES.wonderland,
        eyebrow: "Summer 2026 · Rehearsals · Venice Island PAC",
        // The set-level blurb now describes rehearsals AND both performance
        // nights, which is wrong under this chip. Overridden, not inherited.
        blurb: <>Rehearsal shots from summer 2026 &mdash; Young Performers
          Theatre Camp building its production at Venice Island Performing Arts
          Center. Blocking and choreography in July, then the dress run in
          August.</>,
        stats: [
          { n: n || "—", label: "Photos" },
          { n: "12–18", label: "Ages" },
          { n: "6", label: "Week session" },
        ],
      };
    }
    return {
      eyebrow: `Summer 2026 · ${label} · Venice Island PAC`,
      title: <><em>Wonderland</em><br />{label}.</>,
      blurb: n
        ? <>The Young Performers Theatre Camp production of <em>Wonderland</em>,
          {" "}{label.toLowerCase()} at Venice Island Performing Arts Center. The
          {" "}<a href="#gallery?set=wonderland&loc=rehearsal"
          style={{ color: "var(--c-accent)", textDecoration: "underline" }}>rehearsal
          photos</a> show how it got there.</>
        : <>Photos from {label.toLowerCase()} of <em>Wonderland</em> are still
          being sorted and will be posted here. In the meantime, the
          {" "}<a href="#gallery?set=wonderland&loc=rehearsal"
          style={{ color: "var(--c-accent)", textDecoration: "underline" }}>rehearsal
          photos</a> are up.</>,
      stats: [
        { n: n || "—", label: "Photos" },
        { n: label, label: "Performance" },
        { n: "2026", label: "Season" },
      ],
    };
  }

  const hero = filter === "dell"
    ? dellHero(loc)
    : loc === "all" ? (HEROES[filter] || HEROES.all)
    : filter === "introcamps" ? campHero(loc)
    : filter === "wonderland" ? wonderlandHero(loc)
    : (HEROES[filter] || HEROES.all);

  // ONE slot beside the hero headline, and four different things can claim it.
  //
  //   a named rec center, on either row   that camp's final performance
  //   a rec center WITHOUT a video        nothing; the hero goes full width
  //   any YPTC view                       the Wonderland video, still being edited
  //   All photos / All camps / All night  the FOX 29 segment
  //
  // A camp's own show outranks the news hit ANYWHERE a single camp is named,
  // and that now includes the Dell Finale row. The segment is about the
  // citywide showcase, so it is the right card while the view is the whole
  // night -- but the moment you narrow to Holmesburg, the specific thing on
  // screen is Holmesburg, and their own production is what belongs beside their
  // headline. Before this, picking a camp under Dell Finale left the news
  // segment sitting there: the one card on the page that was not about the camp
  // whose name was in the headline.
  //
  // Both rows use CAMP_LOCATIONS ids, so one lookup serves both. The Dell row
  // is additionally rebuilt from the manifest (see subChips), which means every
  // camp chip it offers has finale-night material -- and the four camps with
  // that material are exactly the four in CAMP_VIDEOS. Athletic, the one camp
  // with no recording, has no Dell chip at all, so the empty case below is
  // unreachable from this row. It stays because the camps row can still reach
  // it, and because that pairing is a fact about the manifest, not a promise.
  //
  // YPTC claims the slot on every one of its views, sub-chip or not -- the
  // recording is of the show, so it is equally the answer under Rehearsal,
  // Night 1 and Night 2. There are two recordings, one per night, and the
  // card follows the night chip: Night 1 opens on Thursday, Night 2 on Friday,
  // and the set-level and rehearsal views open on Thursday with Friday a tap
  // away. See WonderlandPerformanceVideo.
  const namesOneCamp = (filter === "introcamps" || filter === "dell") && loc !== "all";
  const campVideo = namesOneCamp ? CAMP_VIDEOS[loc] : null;
  // What is left for the news card: the two summary views it always had, plus
  // the whole-night view of the finale, which is the segment's actual subject.
  const showNewsCard = filter === "all" ||
    ((filter === "introcamps" || filter === "dell") && loc === "all");
  const heroAside = campVideo
    ? <CampPerformanceVideo
        video={campVideo}
        label={(CAMP_LOCATIONS.find(c => c.id === loc) || {}).label || ""}
      />
    : filter === "wonderland" ? <WonderlandPerformanceVideo loc={loc} />
    : (showNewsCard ? <FoxNewsCard /> : null);

  const driveUrl = (window.useCMS ? window.useCMS("showcase.drive_url", "") : "");
  // showcase.video_ids: comma-separated. Bare token = YouTube ID. "drive:<file_id>" = Google Drive video.
  const VIDEO_DEFAULT = "g8_1641gADQ,i1fxYlFdB_Y";
  const videoIds = (window.useCMS ? window.useCMS("showcase.video_ids", VIDEO_DEFAULT) : VIDEO_DEFAULT);
  const videos = String(videoIds || "").split(",").map(s => s.trim()).filter(Boolean).map(raw => {
    if (raw.startsWith("drive:")) {
      const id = raw.slice(6);
      return { kind: "drive", id, src: `https://drive.google.com/file/d/${id}/preview` };
    }
    return { kind: "youtube", id: raw, src: `https://www.youtube-nocookie.com/embed/${raw}?rel=0&modestbranding=1` };
  });

  return (
    <div>
      <section style={{ background: "var(--c-plum)", color: "var(--c-cream)", padding: "80px 0 64px", borderBottom: "2px solid var(--c-ink)" }}>
        <div className="container">
          {/* Without the modifier the second track is reserved on every set,
              which would squeeze the Wonderland and fireplay headlines against
              400px of nothing. Athletic hits this too now that a camp page can
              have an empty aside. */}
          <div className={heroAside ? "gallery-hero-grid" : "gallery-hero-grid gallery-hero-grid--solo"}>
            <div style={{ minWidth: 0 }}>
              <div className="eyebrow" style={{ color: "var(--c-accent)" }}>{hero.eyebrow}</div>
              <h1 className="display" style={{ fontSize: "clamp(48px, 9vw, 132px)", color: "var(--c-cream)", margin: "12px 0 18px", lineHeight: 0.95 }}>
                {hero.title}
              </h1>
              <p style={{ fontSize: 19, maxWidth: 640, opacity: 0.9, lineHeight: 1.5 }}>
                {hero.blurb}
              </p>
              <div style={{ display: "flex", gap: 28, marginTop: 32, flexWrap: "wrap" }}>
                {hero.stats.map(s => <SmallStat key={s.label} n={s.n} label={s.label} />)}
              </div>
            </div>
            {heroAside}
          </div>
        </div>
        <style>{`
          .gallery-hero-grid {
            display: grid;
            grid-template-columns: minmax(0, 1fr) minmax(300px, 400px);
            gap: 44px;
            align-items: center;
          }
          .gallery-hero-grid--solo { grid-template-columns: 1fr; }
          @media (max-width: 940px) {
            .gallery-hero-grid { grid-template-columns: 1fr; gap: 36px; }
          }
        `}</style>
      </section>

      <section className="section">
        <div className="container">
          <div style={{ display: "flex", gap: 8, marginBottom: 28, flexWrap: "wrap", alignItems: "center", justifyContent: "space-between" }}>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {cats.map(c => (
                <button
                  key={c.id}
                  onClick={() => selectCat(c.id)}
                  aria-pressed={filter === c.id}
                  className="btn small"
                  style={{
                    background: filter === c.id ? "var(--c-ink)" : "var(--c-paper)",
                    color: filter === c.id ? "var(--c-paper)" : "var(--c-ink)",
                  }}
                >
                  {c.label}
                </button>
              ))}
            </div>
            <div style={{ fontFamily: "var(--f-mono)", fontSize: 11, letterSpacing: "0.1em", textTransform: "uppercase", color: "var(--c-ink-soft)" }}>
              {(() => {
                // "26 photos" over a grid holding 20 photos and 6 drone clips
                // is the same lie as the hero stat. Only names the clips when
                // there are some in THIS view.
                const nClips = filtered.filter(isClip).length;
                const nPhotos = filtered.length - nClips;
                if (!nClips) return `${nPhotos} photos`;
                if (!nPhotos) return `${nClips} ${nClips === 1 ? "clip" : "clips"}`;
                return `${nPhotos} photos · ${nClips} ${nClips === 1 ? "clip" : "clips"}`;
              })()}
            </div>
          </div>

          {/* Second row, for the sets that have one. Indented and outlined so it
              reads as narrowing the chip above it rather than as another set. A
              sub-chip with no photos yet is deliberately still clickable --
              hiding it would make five camps look like three, and would hide
              the two Wonderland performances until the night they are posted. */}
          {SUBFILTERS[filter] && (
            <div style={{
              display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center",
              marginTop: -14, marginBottom: 28, marginLeft: 14,
              paddingLeft: 16, borderLeft: "2px solid var(--c-ink)",
            }}>
              <span className="eyebrow" style={{ color: "var(--c-ink-soft)", marginRight: 4 }}>{SUBFILTERS[filter].label}</span>
              {subChips.map(c => (
                <button
                  key={c.id}
                  onClick={() => selectLoc(c.id)}
                  aria-pressed={loc === c.id}
                  className="btn small"
                  style={{
                    background: loc === c.id ? "var(--c-primary-deep)" : "var(--c-paper)",
                    color: loc === c.id ? "var(--c-paper)" : "var(--c-ink)",
                  }}
                >
                  {c.label}
                </button>
              ))}
            </div>
          )}

          {loading && <div style={{ padding: 60, textAlign: "center", color: "var(--c-ink-soft)" }}>Loading photos…</div>}
          {error && !loading && <div style={{ padding: 60, textAlign: "center", color: "var(--c-primary-deep)" }}>Couldn't load photo manifest. ({error})</div>}
          {/* This empty state is public-facing -- it used to read "drop them in
              /img and rebuild the manifest", which is a note to us, shown to
              camp families. Keep any replacement in their language. */}
          {!loading && !error && filtered.length === 0 && <div style={{ padding: 60, textAlign: "center", color: "var(--c-ink-soft)" }}>No photos in this set yet — check back soon.</div>}

          {filtered.length > 0 && (() => {
            const hasMore = filtered.length > GALLERY_PREVIEW_COUNT;
            const visible = (hasMore && !expanded) ? filtered.slice(0, GALLERY_PREVIEW_COUNT) : filtered;
            const hiddenCount = filtered.length - GALLERY_PREVIEW_COUNT;
            return (
              <>
                <div style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(4, 1fr)",
                  gridAutoRows: "180px",
                  gap: 14,
                }} className="masonry">
                  {visible.map((item) => (
                    <button
                      key={item.src}
                      onClick={() => openLightbox(item, undefined, filtered)}
                      aria-label={isClip(item) ? `Play ${item.alt}` : `Open ${item.alt}`}
                      aria-haspopup="dialog"
                      style={{
                        position: "relative",
                        gridColumn: `span ${item.w}`,
                        gridRow: `span ${item.h}`,
                        cursor: "pointer",
                        borderRadius: 14,
                        border: "2px solid var(--c-ink)",
                        overflow: "hidden",
                        padding: 0,
                        background: "var(--c-cream-deep)",
                        transition: "transform 150ms",
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.transform = "translate(-3px, -3px)"}
                      onMouseLeave={(e) => e.currentTarget.style.transform = "translate(0, 0)"}
                    >
                      {isClip(item)
                        ? <AerialTile item={item} />
                        : <img src={item.src} alt={item.alt} loading="lazy"
                            style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />}
                    </button>
                  ))}
                </div>
                {hasMore && (
                  <div style={{ display: "flex", justifyContent: "center", marginTop: 28 }}>
                    <button
                      type="button"
                      onClick={() => setExpanded(v => !v)}
                      aria-expanded={expanded}
                      className="btn small"
                      style={{ background: "var(--c-ink)", color: "var(--c-paper)" }}
                    >
                      {expanded
                        ? <>Show fewer <span aria-hidden="true">↑</span></>
                        : <>See {hiddenCount} more {hiddenCount === 1 ? "photo" : "photos"} <span aria-hidden="true">↓</span></>}
                    </button>
                  </div>
                )}
              </>
            );
          })()}

          {(filter === "all" || filter === "wonderland") && <WonderlandCard />}
          {(filter === "all" || filter === "firesafety") && <FirePlayCard />}

          {driveUrl && (
            <div style={{ marginTop: 40, padding: 24, border: "2px solid var(--c-ink)", borderRadius: 18, background: "var(--c-cream-deep)", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
              <div>
                <div className="eyebrow">Source archive</div>
                <div className="display" style={{ fontSize: 22, marginTop: 4 }}>Venice Island — 4/28/26 Show</div>
                <div style={{ fontSize: 13, color: "var(--c-ink-soft)", marginTop: 4 }}>Full Google Drive folder with all photos and B-roll video.</div>
              </div>
              <a href={driveUrl} target="_blank" rel="noopener" className="btn small ochre">Open Drive folder ↗</a>
            </div>
          )}

          {/* "All photos" only, same rule as the film band and Star Players
              below: a 4/25/2024 Citywide Dance Festival album is not part of
              any 2026 set, and sitting under a rec center's camp photos it
              reads as more of that camp's. */}
          {filter === "all" && <FlickrAlbumCard />}
          {/* Renzolution shot the showcase, the Wonderland and the Intro Camp
              sets, NOT the Fire Prevention Play set (Joe O'Connor, credited on
              FirePlayCard). Hiding this on that filter is the whole reason it is
              conditional -- a credit block sitting under someone else's photos is
              a misattribution, not a layout preference. The camps set is theirs
              on the same evidence: the source Drive folders J.J. sent are owned
              by renzolutionphotography@gmail.com. */}
          {filter !== "firesafety" && <PhotographerCredit />}
        </div>

        <style>{`
          @media (max-width: 980px) { .masonry { grid-template-columns: repeat(3, 1fr) !important; } }
          @media (max-width: 700px) { .masonry { grid-template-columns: repeat(2, 1fr) !important; grid-auto-rows: 140px !important; } }
        `}</style>
      </section>

      {/* "All photos" only, same rule as Star Players below. Both are ABOUT THE
          ARCHIVE rather than about any one set: these are 2024 and 2025 shows
          from Cobbs Creek and Myers, so under a 2026 rec center's photos they
          read as that camp's own footage. Narrowing to a center is someone
          looking for their kid, and everything on screen should be that camp. */}
      {filter === "all" && videos.length > 0 && (
        <section style={{ background: "var(--c-paper)", borderTop: "2px solid var(--c-ink)", borderBottom: "2px solid var(--c-ink)", padding: "64px 0" }}>
          <div className="container">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: 24, flexWrap: "wrap", gap: 16 }}>
              <div>
                <div className="eyebrow">Watch the showcase</div>
                <div className="display" style={{ fontSize: "clamp(32px, 4.5vw, 56px)", marginTop: 10, lineHeight: 0.98 }}>
                  Past PPR shows on film.
                </div>
                <p style={{ fontSize: 16, color: "var(--c-ink-soft)", marginTop: 12, maxWidth: 640, lineHeight: 1.55 }}>
                  Cobbs Creek 2025 Intro Camp Performance of Beauty and the Beast,
                  and Myers 2024 Intro Camp Performance of High School Musical.
                  These are the kinds of nights our young performers build toward across the summer.
                </p>
              </div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 20 }} className="video-grid">
              {videos.map(v => (
                <div key={v.id} style={{
                  aspectRatio: "16 / 9", border: "2px solid var(--c-ink)", borderRadius: 14,
                  overflow: "hidden", background: "var(--c-cream-deep)"
                }}>
                  <iframe
                    src={v.src}
                    title={`PPR Performing Arts showcase video ${v.id}`}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    referrerPolicy="strict-origin-when-cross-origin"
                    allowFullScreen
                    loading="lazy"
                    style={{ width: "100%", height: "100%", border: 0, display: "block" }}
                  />
                </div>
              ))}
            </div>
            <style>{`
              @media (max-width: 800px) { .video-grid { grid-template-columns: 1fr !important; } }
            `}</style>
          </div>
        </section>
      )}

      {/* CR #273: Star Players of Philadelphia -- pinned section at the bottom
          of the gallery. Bill Arthur (Holmesburg) runs the group; the link
          target is the section's #star-players anchor (cross-linked from his
          committee bio panel on /#about).

          "All photos" only. Star Players is its own group, not a PPR camp, so
          under Kendrick's Intro Camp photos it reads as Kendrick's. The /#about
          cross-link still lands: it navigates to a bare "#gallery" with no
          ?set=, which loads on "all", so the anchor is on the page when the
          scroll runs. Keep it that way -- adding a ?set= to that link would
          scroll to a section that is no longer rendered. */}
      {filter === "all" && (
      <section
        id="star-players"
        style={{
          background: "var(--c-cream-deep)",
          borderTop: "2px solid var(--c-ink)",
          borderBottom: "2px solid var(--c-ink)",
          padding: "72px 0",
          scrollMarginTop: 88,
        }}
      >
        <div className="container">
          <div style={{ maxWidth: 760 }}>
            <div className="eyebrow" style={{ color: "var(--c-primary-deep)" }}>Spotlight</div>
            <div className="display" style={{ fontSize: "clamp(32px, 4.5vw, 56px)", marginTop: 10, lineHeight: 1 }}>
              Star Players of Philadelphia.
            </div>
            <p style={{ fontSize: 16.5, color: "var(--c-ink-soft)", marginTop: 18, lineHeight: 1.65 }}>
              Founded in 2013, the Star Players are a theater arts group for the
              Philadelphia Parks &amp; Recreation community. Their mission is to
              bring cultural activities to children and adults of every background
              through creative expression &mdash; giving participants the chance to
              perform in front of a real audience and showcase their talents.
            </p>
            <p style={{ fontSize: 16.5, color: "var(--c-ink-soft)", marginTop: 14, lineHeight: 1.65 }}>
              The group develops acting, singing, and dancing skills through hands-on
              instruction, and runs alongside the broader PPR programming calendar.
              Group activities include productions of musicals, dramas, comedies,
              and cabarets, plus special classes and workshops throughout the year.
            </p>
          </div>

          <div className="star-players-grid" style={{ marginTop: 36 }}>
            {STAR_PLAYERS.map((p) => (
              <button
                key={p.src}
                type="button"
                onClick={() => openLightbox(p, "starplayers", STAR_PLAYERS)}
                style={{
                  appearance: "none",
                  padding: 0,
                  border: "2px solid var(--c-ink)",
                  borderRadius: 14,
                  overflow: "hidden",
                  background: "var(--c-paper)",
                  cursor: "zoom-in",
                  aspectRatio: "4 / 3",
                  boxShadow: "var(--shadow-lift)",
                }}
              >
                <img
                  src={p.src}
                  alt={p.alt}
                  loading="lazy"
                  style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                />
              </button>
            ))}
          </div>

          <div
            className="star-players-contact"
            style={{
              marginTop: 36,
              padding: 24,
              border: "2px solid var(--c-ink)",
              borderRadius: 18,
              background: "var(--c-paper)",
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 20,
              boxShadow: "var(--shadow-lift)",
            }}
          >
            <div>
              <div className="eyebrow">Home base</div>
              <div style={{ fontSize: 17, fontWeight: 600, marginTop: 8 }}>Holmesburg Recreation Center</div>
              <a
                href="tel:2156858714"
                style={{ display: "inline-block", marginTop: 10, color: "var(--c-primary-deep)", fontWeight: 600, fontSize: 16 }}
              >
                215-685-8714
              </a>
              <div style={{ fontSize: 11.5, color: "var(--c-ink-soft)", marginTop: 4, fontFamily: "var(--f-mono)", letterSpacing: "0.05em" }}>
                Holmesburg direct line
              </div>
            </div>
            <div>
              <div className="eyebrow">Get involved</div>
              <a
                href="mailto:Bill.arthur@phila.gov"
                style={{ display: "block", marginTop: 8, color: "var(--c-ink)", fontWeight: 600 }}
              >
                Bill.arthur@phila.gov
              </a>
              <a
                href="https://www.facebook.com/starplayersPPR/"
                target="_blank"
                rel="noopener"
                className="btn small ghost"
                style={{ marginTop: 14, display: "inline-flex", alignItems: "center", gap: 8 }}
              >
                Star Players on Facebook ↗
              </a>
            </div>
          </div>

          <style>{`
            .star-players-grid {
              display: grid;
              grid-template-columns: repeat(4, 1fr);
              gap: 16px;
            }
            @media (max-width: 900px) {
              .star-players-grid { grid-template-columns: repeat(2, 1fr); }
              .star-players-contact { grid-template-columns: 1fr !important; }
            }
          `}</style>
        </div>
      </section>
      )}

      {lightbox && (() => {
        const current = lightbox.list[lightbox.index];
        const many = lightbox.list.length > 1;
        const dl = downloadFor(current);
        const stop = (e) => e.stopPropagation();
        const arrow = (side) => ({
          position: "absolute", [side]: 10, top: "50%", transform: "translateY(-50%)",
          background: "var(--c-paper)", border: "2px solid var(--c-ink)", borderRadius: 999,
          width: 44, height: 44, fontSize: 20, fontWeight: 800, cursor: "pointer", zIndex: 2,
        });
        return (
        <div
          onClick={() => setLightbox(null)}
          role="dialog"
          aria-modal="true"
          aria-label={current.alt}
          style={{ position: "fixed", inset: 0, background: "rgba(42,24,32,0.92)", zIndex: 100, display: "flex", alignItems: "center", justifyContent: "center", padding: 32, cursor: "zoom-out" }}
        >
          {/* The drone clips carry NO AUDIO TRACK at all (the encode strips it,
              and the DJI source has none), so this is not the "now with sound"
              copy -- it is the full-size one, with a scrub bar and a fullscreen
              button the 2-column tile has no room for. Keep `muted` off anyway:
              a video element with no audio track and muted set is the same
              thing to a browser, and hardcoding it would be a lie the day a
              clip with sound is added. autoPlay is safe here, it follows a
              click. stopPropagation on the wrapper keeps the scrub bar from
              closing the overlay on every drag -- the backdrop still closes. */}
          {isClip(current) ? (
            <div onClick={stop} style={{ width: "min(1100px, 92vw)", cursor: "default", position: "relative" }}>
              <video
                key={current.src}
                src={current.src}
                poster={current.poster}
                controls
                autoPlay
                loop
                playsInline
                aria-label={current.alt}
                style={{ width: "100%", maxHeight: "82vh", borderRadius: 12, border: "2px solid var(--c-paper)", background: "var(--c-plum)", display: "block" }}
              />
              {/* A clip's download sits UNDER the video, not on it: the bottom
                  right corner of a <video controls> is the fullscreen button,
                  and a link laid over it would eat the click. */}
              <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 12 }}>
                <div style={{ flex: 1, color: "var(--c-cream)", fontFamily: "var(--f-mono)", fontSize: 12, letterSpacing: "0.1em", textTransform: "uppercase" }}>
                  {current.caption || ""}
                </div>
                <a
                  href={dl.href}
                  download={dl.name}
                  onClick={stop}
                  aria-label={dl.label}
                  className="btn small ochre"
                  style={{ textDecoration: "none", flex: "0 0 auto" }}
                >
                  {dl.label} ↓
                </a>
              </div>
            </div>
          ) : (
            /* THE WRAPPER IS WHAT PUTS THE BUTTON ON THE PHOTO. Sizing the img
               by max-width/max-height rather than a fixed width means the
               element box IS the picture -- no letterboxed dead space beside a
               portrait shot for a corner-anchored control to float in. */
            <div onClick={stop} style={{ position: "relative", display: "inline-block", lineHeight: 0, maxWidth: "min(1100px, 92vw)", maxHeight: "88vh" }}>
              <img
                src={current.src}
                alt={current.alt}
                style={{ display: "block", maxWidth: "min(1100px, 92vw)", maxHeight: "88vh", width: "auto", height: "auto", borderRadius: 12, border: "2px solid var(--c-paper)" }}
              />
              {/* Download, bottom right of the photo. Same origin, so the
                  `download` attribute is honoured and the file lands with its
                  own name instead of opening in a tab -- which is the entire
                  fix on iOS, where the old answer was a long-press and a menu
                  that does not always offer save. */}
              <a
                href={dl.href}
                download={dl.name}
                onClick={stop}
                aria-label={dl.label}
                className="btn small ochre"
                style={{ position: "absolute", right: 12, bottom: 12, textDecoration: "none", lineHeight: "normal", zIndex: 2 }}
              >
                {dl.label} ↓
              </a>
            </div>
          )}

          {many && (
            <React.Fragment>
              <button onClick={(e) => { stop(e); stepLightbox(-1); }} aria-label="Previous photo" style={arrow("left")}>‹</button>
              <button onClick={(e) => { stop(e); stepLightbox(1); }} aria-label="Next photo" style={arrow("right")}>›</button>
              {/* Position in the set. Without it there is no way to tell a
                  40-photo album from a 400-photo one until you have arrowed
                  through it. */}
              <div style={{
                position: "absolute", bottom: 22, left: "50%", transform: "translateX(-50%)",
                color: "var(--c-cream)", fontFamily: "var(--f-mono)", fontSize: 12,
                letterSpacing: "0.1em", background: "rgba(42,24,32,0.72)",
                padding: "6px 12px", borderRadius: 999, pointerEvents: "none",
              }}>
                {lightbox.index + 1} / {lightbox.list.length}
              </div>
            </React.Fragment>
          )}

          <button onClick={() => setLightbox(null)} aria-label="Close" style={{
            position: "absolute", top: 24, right: 24, background: "var(--c-paper)",
            border: "2px solid var(--c-ink)", borderRadius: 999,
            width: 44, height: 44, fontSize: 20, fontWeight: 800, cursor: "pointer", zIndex: 2,
          }}>×</button>
        </div>
        );
      })()}
    </div>
  );
}

function SmallStat({ n, label }) {
  return (
    <div>
      <div className="display" style={{ fontSize: 36, color: "var(--c-accent)", lineHeight: 1 }}>{n}</div>
      <div style={{ fontFamily: "var(--f-mono)", fontSize: 11, letterSpacing: "0.12em", textTransform: "uppercase", marginTop: 4, opacity: 0.7 }}>{label}</div>
    </div>
  );
}

/* An aerial clip sitting in the photo grid. It is a MOVING PHOTOGRAPH: muted,
   looping, no controls, no sound, no way to make noise by scrolling past it.
   Clicking opens the same lightbox a photo opens, and that copy has sound and
   controls (see the lightbox in GalleryPage).

   Three things keep this from being expensive, and all three matter on a phone
   on rec center wifi:

     preload="none" until seen   the poster carries the tile; the mp4 is not
                                 fetched at all until the tile is on screen.
                                 "All camps" can hold six of these.
     paused when off screen      scrolling past does not leave five decoders
                                 running behind you.
     never on save-data or       a visitor who has asked their browser not to
     reduced-motion              autoplay video, or not to burn data, gets the
                                 poster and the play badge. The click path is
                                 identical, so nothing is unreachable.

   No IntersectionObserver (very old browser) means the poster stays and the
   clip plays only in the lightbox -- the same graceful floor. */
function AerialTile({ item }) {
  const ref = React.useRef(null);

  React.useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const mm = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)");
    const conn = navigator.connection || {};
    if ((mm && mm.matches) || conn.saveData) return;
    if (typeof IntersectionObserver === "undefined") return;

    const io = new IntersectionObserver((entries) => {
      entries.forEach(e => {
        if (e.isIntersecting) {
          // Assigning preload is what starts the download. Doing it here rather
          // than in the markup is the whole point of the observer.
          el.preload = "auto";
          const p = el.play();
          // Autoplay can still be refused (a browser setting, low power mode).
          // Swallow it -- the poster is already the fallback.
          if (p && p.catch) p.catch(() => {});
        } else {
          el.pause();
        }
      });
    }, { threshold: 0.4 });

    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <>
      <video
        ref={ref}
        poster={item.poster}
        preload="none"
        muted
        loop
        playsInline
        aria-hidden="true"
        tabIndex={-1}
        style={{ width: "100%", height: "100%", objectFit: "cover", display: "block", background: "var(--c-plum)" }}
      >
        <source src={item.src} type="video/mp4" />
      </video>
      <span aria-hidden="true" style={{
        position: "absolute", top: 10, left: 10,
        display: "inline-flex", alignItems: "center", gap: 6,
        fontFamily: "var(--f-mono)", fontSize: 10, letterSpacing: "0.12em",
        textTransform: "uppercase", fontWeight: 700,
        background: "var(--c-primary)", color: "var(--c-paper)",
        padding: "5px 10px", borderRadius: 999,
      }}>Aerial</span>
      {/* The tile is silent, so the caption is what tells you whose camp this
          is -- the same job the alt text does for a photo. */}
      <span aria-hidden="true" style={{
        position: "absolute", left: 0, right: 0, bottom: 0,
        display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10,
        padding: "26px 12px 10px", textAlign: "left",
        background: "linear-gradient(to top, rgba(42,24,32,0.92), rgba(42,24,32,0))",
        color: "var(--c-cream)", fontWeight: 700, fontSize: 14,
      }}>
        {item.caption || "Aerial"}
        <span style={{ fontSize: 12, opacity: 0.85, whiteSpace: "nowrap" }}>▶ Play</span>
      </span>
    </>
  );
}

/* A rec center's final production, in the hero slot beside its headline --
   the same slot FoxNewsCard takes on the two summary views, which is why this
   is built to that card's proportions rather than as a full-width band.
   Deliberately matched: at ~400px the two are interchangeable furniture, and a
   camp page should not restyle the hero just because its aside is a video.

   Drive facade, not a bare /preview iframe. Firecamp.jsx has a sibling of this
   for the Fire Prevention Play submissions and the reasoning is the same there:
   the raw iframe is a poor player (small play target on a phone, occasional
   "Preview unavailable"), and every page load would spend the file's Drive view
   quota whether or not anyone pressed play. The thumbnail carries the card and
   the iframe mounts on the first tap. The two are deliberately NOT one shared
   component -- that one is a small card in a voting grid with its own labels,
   this one is a full-width section, and merging them would mean a props bag
   that serves neither. Each .jsx here is compiled as its own script and only
   reaches other files through window, so sharing it would also mean exporting
   across two unrelated pages.

   These are 1080p .mov masters on someone else's Drive, so they are not served
   from this repo and are not in the deploy. Drive does the transcoding. The
   trade is that we do not control the uptime -- hence the plain link out, which
   works when the embed does not.

   THE IFRAME NO LONGER PLAYS IN THE CARD. It used to, and that was the whole
   problem J.J. reported: this slot is capped at 400px by .gallery-hero-grid, so
   a 16:9 embed is ~225px tall, and Drive's player DROPS controls as it narrows
   -- you get a scrub bar and nothing else, no volume, no captions, barely a
   fullscreen target. Pressing play now opens a theater overlay instead and the
   player gets min(1200px, 94vw), which is the width at which Drive renders its
   full control bar. The card keeps the poster so nothing about the hero moves.

   Two things this deliberately does NOT try to solve, so nobody re-opens them:
   iOS gives no page or iframe any control over media volume (hardware buttons
   only, enforced by the OS), and a phone held in portrait cannot make a 16:9
   frame taller than ~9/16 of its width no matter who owns the player. Rotating
   or going fullscreen is the answer to both, hence the fullscreen button. */
function CampPerformanceVideo({ video, label }) {
  return (
    <PerformanceVideoCard
      video={video}
      label={label}
      title={`${label} Intro Camp — ${video.show}`}
      collection="introcamps"
      eyebrow="Final performance"
      headline={<>{label} presents <em>{video.show}</em>.</>}
      blurb={<>The full show, closing out the 2026 Intro Camp session at {label}
        {" "}Recreation Center.</>}
    />
  );
}

/* The card itself, shared by the camp recordings and the Wonderland nights
   (2026-10-08, when the Wonderland files landed). Everything a visitor reads
   on it comes in as props; the poster, play facade, theater, Drive link and
   media tracking are the same for both. `children` renders under the blurb,
   above the Drive link -- the Wonderland night switch lives there. */
function PerformanceVideoCard({ video, label, title, collection, eyebrow, headline, blurb, children }) {
  const [playing, setPlaying] = React.useState(false);
  const [thumbOk, setThumbOk] = React.useState(true);
  const frameRef = React.useRef(null);

  // Esc closes, and the page behind the overlay does not scroll under it.
  // Both only exist while the overlay is up, so the gallery is untouched the
  // rest of the time.
  React.useEffect(() => {
    if (!playing) return;
    const onKey = (e) => { if (e.key === "Escape") setPlaying(false); };
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [playing]);

  // Drive's own control bar carries a fullscreen button, but it is the smallest
  // target in the bar and on a phone it is the control that actually matters.
  // This is the same request against our wrapper, at 44px. Safari on iOS does
  // not implement it for an iframe, so the button hides itself there rather
  // than sitting on the overlay doing nothing.
  const canFullscreen = typeof document !== "undefined" &&
    (document.fullscreenEnabled || document.webkitFullscreenEnabled);
  const goFullscreen = () => {
    const el = frameRef.current;
    if (!el) return;
    (el.requestFullscreen || el.webkitRequestFullscreen || (() => {})).call(el);
  };

  return (
    <div style={{
      border: "2px solid var(--c-ink)",
      borderRadius: 18,
      overflow: "hidden",
      background: "var(--c-ink)",
      color: "var(--c-cream)",
      boxShadow: "var(--shadow-lift)",
    }}>
      <button
        type="button"
        onClick={() => {
          setPlaying(true);
          // "drive:<id>" and not the embed URL: the id is what survives a
          // change of player. Reported here, on the press, rather than inside
          // the theater -- the theater remounts on every fullscreen toggle.
          if (window.pprTrackMedia) {
            window.pprTrackMedia({ id: `drive:${video.id}`, kind: "video", title, collection });
          }
        }}
        aria-label={`Play ${title}`}
        aria-haspopup="dialog"
        style={{
          // padding BEFORE paddingTop: the shorthand resets every side, so the
          // other order zeroes the aspect-ratio spacer and the button
          // collapses to nothing.
          display: "block", position: "relative", width: "100%",
          border: 0, padding: 0, paddingTop: "56.25%",
          background: "var(--c-plum)", cursor: "pointer",
        }}
      >
        {thumbOk && (
          <img
            src={`https://drive.google.com/thumbnail?id=${video.id}&sz=w1000`}
            alt=""
            loading="lazy"
            onError={() => setThumbOk(false)}
            style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }}
          />
        )}
        {/* Sized down from the full-width band this used to be: at ~400px a
            72px button and a second line of label crowded the frame. */}
        <span style={{
          position: "absolute", inset: 0, display: "flex", flexDirection: "column",
          alignItems: "center", justifyContent: "center", gap: 8,
          color: "var(--c-cream)", background: "rgba(42,24,32,0.42)",
        }}>
          <span aria-hidden="true" style={{
            width: 56, height: 56, borderRadius: "50%", background: "var(--c-primary)",
            display: "flex", alignItems: "center", justifyContent: "center",
            paddingLeft: 3, border: "2px solid var(--c-cream)",
          }}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" focusable="false" aria-hidden="true">
              <path d="M8 5v14l11-7z" />
            </svg>
          </span>
          <span style={{ fontWeight: 700, fontSize: 13, textShadow: "0 1px 6px rgba(0,0,0,0.6)" }}>
            Watch the performance
          </span>
        </span>
      </button>
      {playing && <CampVideoTheater
        video={video}
        title={title}
        label={label}
        frameRef={frameRef}
        canFullscreen={canFullscreen}
        onFullscreen={goFullscreen}
        onClose={() => setPlaying(false)}
      />}
      <div style={{ padding: "18px 20px 20px" }}>
        <div className="eyebrow" style={{ color: "var(--c-accent)" }}>{eyebrow}</div>
        <div className="display" style={{ fontSize: 21, marginTop: 8, lineHeight: 1.15, color: "var(--c-cream)" }}>
          {headline}
        </div>
        <p style={{ fontSize: 13.5, color: "rgba(251,243,226,0.72)", marginTop: 8, lineHeight: 1.5 }}>
          {blurb}
        </p>
        {children}
        {/* The embed depends on Drive being reachable and on the file staying
            link-shared. When it is not, this is the only way through. */}
        <a
          href={`https://drive.google.com/file/d/${video.id}/view`}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: "inline-block", marginTop: 14,
            fontFamily: "var(--f-mono)", fontSize: 11, letterSpacing: "0.1em",
            textTransform: "uppercase", color: "var(--c-accent)",
            fontWeight: 700, textDecoration: "underline",
          }}
        >
          Open on Google Drive
        </a>
      </div>
    </div>
  );
}

/* The Wonderland recording, in the same slot as the camp cards. Until
   2026-10-08 this was WonderlandVideoPending, a "Coming soon" card holding the
   slot so YPTC's header would not read as "there is no video" while the show
   was being cut (WONDERLAND_SETS applies the same rule to the night chips).
   The recordings landed, that card is gone, and this is the real player --
   which is why the pending card was told never to grow a playing state.

   TWO NIGHTS, ONE CARD. Unlike a camp, YPTC played twice and both nights were
   filmed end to end, and the slot is capped at 400px by .gallery-hero-grid, so
   two players would halve each poster to a strip. The card holds one player
   and a two-button night switch under the headline. The night follows the
   chip row: under Night 1 or Night 2 the card opens on that night, under All
   Wonderland or Rehearsal it opens on Thursday (opening night) with Friday one
   tap away. Switching never opens the theater: the card remounts on the new
   file (key={night}), which swaps the poster and resets the play state, so a
   visitor picks a night and then presses play.

   Poster is the Drive thumbnail of the chosen night, same as the camp cards.
   WONDERLAND_VIDEO_POSTER, the local still the pending card showed, is gone
   with it. */
function WonderlandPerformanceVideo({ loc }) {
  const fromChip = WONDERLAND_VIDEOS[loc] ? loc : "night1";
  const [night, setNight] = React.useState(fromChip);
  // The chip row is the stronger signal: picking Night 2 up top must not leave
  // the card on Thursday because someone pressed Thursday earlier.
  React.useEffect(() => { setNight(fromChip); }, [fromChip]);
  const video = WONDERLAND_VIDEOS[night] || WONDERLAND_VIDEOS.night1;

  return (
    <PerformanceVideoCard
      key={night}
      video={video}
      label="YPTC"
      title={`YPTC presents Wonderland — ${video.when}`}
      collection="wonderland"
      eyebrow="Full performance"
      headline={<>YPTC presents <em>Wonderland</em>.</>}
      blurb={<>The full show from {video.when}, 2026, at Venice Island
        Performing Arts Center. Both nights were filmed. Pick one below.</>}
    >
      <div role="group" aria-label="Choose a night" style={{ display: "flex", gap: 8, marginTop: 14, flexWrap: "wrap" }}>
        {Object.keys(WONDERLAND_VIDEOS).map(id => {
          const v = WONDERLAND_VIDEOS[id];
          const on = id === night;
          return (
            <button
              key={id}
              type="button"
              aria-pressed={on}
              onClick={() => setNight(id)}
              style={{
                fontFamily: "var(--f-mono)", fontSize: 11, letterSpacing: "0.1em",
                textTransform: "uppercase", fontWeight: 700,
                padding: "8px 12px", borderRadius: 999, cursor: "pointer",
                border: "2px solid var(--c-cream)",
                background: on ? "var(--c-cream)" : "transparent",
                color: on ? "var(--c-ink)" : "var(--c-cream)",
              }}
            >
              {v.chip} &middot; {v.when.split(",")[0]}
            </button>
          );
        })}
      </div>
    </PerformanceVideoCard>
  );
}

/* The overlay CampPerformanceVideo opens. Portalled to <body> on purpose: the
   card it is launched from sets overflow:hidden and a border radius, and it
   sits inside the hero grid, so rendering in place would put this at the mercy
   of an ancestor for clipping and stacking. Nothing else on this page renders
   above z-index 100 (the photo lightbox), so 120 clears it.

   The iframe mounts with the overlay and unmounts with it. That is what stops
   the audio: there is no way to pause another origin's player from here, so
   closing has to take the document with it. */
function CampVideoTheater({ video, title, label, frameRef, canFullscreen, onFullscreen, onClose }) {
  return ReactDOM.createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title}
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, zIndex: 120,
        background: "rgba(42,24,32,0.94)",
        display: "flex", flexDirection: "column",
        alignItems: "center", justifyContent: "center",
        gap: 14, padding: "clamp(12px, 4vw, 40px)",
      }}
    >
      {/* 1200px is past the width where Drive stops hiding controls, and 94vw
          keeps a phone from putting the control bar under the screen edge.
          maxHeight is what makes a laptop in a short window still show the bar
          instead of pushing it below the fold. */}
      <div
        ref={frameRef}
        onClick={(e) => e.stopPropagation()}
        style={{
          position: "relative", width: "min(1200px, 94vw)",
          aspectRatio: "16 / 9", maxHeight: "78vh",
          background: "#000", borderRadius: 10, overflow: "hidden",
          border: "2px solid var(--c-cream)",
        }}
      >
        <iframe
          src={`https://drive.google.com/file/d/${video.id}/preview`}
          title={title}
          style={{ position: "absolute", inset: 0, width: "100%", height: "100%", border: 0 }}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      </div>

      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap",
          justifyContent: "center", color: "var(--c-cream)", textAlign: "center",
        }}
      >
        <span style={{ fontSize: 14, fontWeight: 600 }}>
          {label} presents <em>{video.show}</em>{video.when ? <> &middot; {video.when}</> : null}
        </span>
        {canFullscreen && (
          <button type="button" onClick={onFullscreen} className="btn small">
            Full screen
          </button>
        )}
        <a
          href={`https://drive.google.com/file/d/${video.id}/view`}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            fontFamily: "var(--f-mono)", fontSize: 11, letterSpacing: "0.1em",
            textTransform: "uppercase", color: "var(--c-accent)",
            fontWeight: 700, textDecoration: "underline",
          }}
        >
          Open on Google Drive
        </a>
      </div>

      <button
        type="button"
        onClick={onClose}
        aria-label="Close video"
        style={{
          position: "absolute", top: 20, right: 20, background: "var(--c-paper)",
          border: "2px solid var(--c-ink)", borderRadius: 999,
          width: 44, height: 44, fontSize: 20, fontWeight: 800, cursor: "pointer",
        }}
      >
        ×
      </button>
    </div>,
    document.body
  );
}

/* Context for the `wonderland` photos in the manifest: the Young Performers
   Theatre Camp (ages 12-18) building its 2026 production at Venice Island.
   The two sets are the July 18 rehearsal and the August 4 dress run.
   The ticket CTA retires itself after the last performance -- same date the
   home-page popup uses, out of window.WONDERLAND. After that the card stays
   put and just reads in past tense, since the photos still want the context. */
function WonderlandCard() {
  const w = window.WONDERLAND || {};
  const stillRunning = w.lastShow ? new Date() < new Date(w.lastShow) : false;

  return (
    <div style={{
      marginTop: 40,
      padding: 28,
      border: "2px solid var(--c-ink)",
      borderRadius: 18,
      background: "var(--c-plum)",
      color: "var(--c-cream)",
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      gap: 18,
      flexWrap: "wrap",
      boxShadow: "var(--shadow-lift)",
    }}>
      <div style={{ flex: "1 1 340px", minWidth: 0 }}>
        <div className="eyebrow" style={{ color: "var(--c-accent)" }}>Building Wonderland</div>
        <div className="display" style={{ fontSize: "clamp(24px, 3.4vw, 34px)", marginTop: 8, lineHeight: 1.05, color: "var(--c-cream)" }}>
          Young Performers Theatre Camp presents <em>Wonderland</em>.
        </div>
        <p style={{ fontSize: 15, marginTop: 14, lineHeight: 1.6, color: "rgba(251,243,226,0.82)", maxWidth: 560 }}>
          YPTC campers, ages 12&ndash;18, spend a six-week session at Venice Island
          Performing Arts Center building one full production from scratch. These
          are the rooms it got made in &mdash; blocking and choreography on July 18,
          then the dress run on August 4.
        </p>
        <p style={{ fontSize: 15, marginTop: 12, lineHeight: 1.6, color: "rgba(251,243,226,0.82)", maxWidth: 560 }}>
          {stillRunning
            ? <>Two performances, backed by a live band: <strong>August 6 and 7 at 6:30PM</strong>, 7 Lock Street, Manayunk.</>
            : <>It ran August 6 and 7, 2026, backed by a live band, at 7 Lock Street in Manayunk.</>}
        </p>
        {/* Pre-show only now. This used to flip to "More photos from the
            performances are being added here" once the run was over, which was
            true for the week it took to get them up and has been false since
            both nights landed -- all 650 are on the Night 1 and Night 2 chips
            above it, so the line was promising the visitor something the page
            had already given them. Nothing replaces it: the two chips and their
            counts say it better than a sentence can.
            The pre-show branch stays, and stays date-driven off w.lastShow, so
            next season's production gets the promise back without an edit. */}
        {stillRunning && (
          <p style={{ fontSize: 15, marginTop: 12, lineHeight: 1.6, color: "var(--c-accent)", maxWidth: 560, fontWeight: 600 }}>
            More photos will be added here after the show dates.
          </p>
        )}
      </div>
      {stillRunning && w.ticketsUrl && (
        <div style={{ display: "flex", flexDirection: "column", gap: 8, alignItems: "flex-start" }}>
          <a href={w.ticketsUrl} target="_blank" rel="noopener noreferrer" className="btn small ochre">
            Tickets &mdash; $10 &#8599;
          </a>
          <span style={{ fontFamily: "var(--f-mono)", fontSize: 11, letterSpacing: "0.08em", textTransform: "uppercase", color: "rgba(251,243,226,0.6)" }}>
            Via TicketLeap
          </span>
        </div>
      )}
    </div>
  );
}

/* Context for the `firesafety` photos: the citywide Fire Prevention Play
   competition, 7/29/26 at the Free Library. Doubles as the credit block for
   that set -- these are Joe O'Connor's, shot for the Philadelphia Parks &
   Recreation Flickr account, not Renzolution's, so PhotographerCredit is
   suppressed on this filter. If a later set arrives from a third photographer,
   give it its own card rather than widening either of these.
   The source Flickr album uses different wording for this event. J.J. wants
   the site to say Fire Prevention Play everywhere, so the album's own title is
   deliberately not echoed here, in the filenames, or in any alt text. These
   .jsx files are served raw and compiled in the browser, so a comment here is
   public -- do not paste the album title in to explain the rule. */
function FirePlayCard() {
  return (
    <div style={{
      marginTop: 40,
      padding: 28,
      border: "2px solid var(--c-ink)",
      borderRadius: 18,
      background: "var(--c-plum)",
      color: "var(--c-cream)",
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      gap: 18,
      flexWrap: "wrap",
      boxShadow: "var(--shadow-lift)",
    }}>
      <div style={{ flex: "1 1 340px", minWidth: 0 }}>
        <div className="eyebrow" style={{ color: "var(--c-accent)" }}>Citywide final</div>
        <div className="display" style={{ fontSize: "clamp(24px, 3.4vw, 34px)", marginTop: 8, lineHeight: 1.05, color: "var(--c-cream)" }}>
          The Fire Prevention Play.
        </div>
        <p style={{ fontSize: 15, marginTop: 14, lineHeight: 1.6, color: "rgba(251,243,226,0.82)", maxWidth: 560 }}>
          One play from each of the 12 PPR districts advanced to the citywide
          competition on July 29 at the Free Library, 1901 Vine Street, presented
          by the Philadelphia Fire Department and Philadelphia Parks &amp;
          Recreation. Placements, awards and every play on video are on the
          Fire Prevention Play page.
        </p>
        <p style={{ fontFamily: "var(--f-mono)", fontSize: 11.5, letterSpacing: "0.08em", textTransform: "uppercase", color: "rgba(251,243,226,0.6)", marginTop: 14 }}>
          Photos by Joe O&rsquo;Connor
        </p>
      </div>
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
        <a href="#fireplay" className="btn small ochre">
          Results &amp; videos &rarr;
        </a>
        <a
          href="https://www.flickr.com/photos/77365596@N05/albums/72177720335038489"
          target="_blank"
          rel="noopener"
          className="btn small ghost"
          style={{ color: "var(--c-cream)", borderColor: "var(--c-cream)" }}
        >
          Full album on Flickr &#8599;
        </a>
      </div>
    </div>
  );
}

/* FOX 29's segment on the 8/10/26 Summer Camp Showcase at the Dell -- the
   citywide finale of the Intro Camp season, so it rides beside that hero.

   HOSTED HERE WITH WTXF'S PERMISSION, granted 2026-08-11. That permission is
   the ONLY reason this is a <video> and not a link to fox29.com, so it is not
   a detail to lose: if anyone ever asks why a broadcaster's segment is being
   served off a City department's site, the answer is that email. Keep the FOX
   29 credit and the link to their page on the card regardless -- attribution
   is a condition of the courtesy, not decoration.

   The file is re-encoded from FOX's 85MB/5Mbps syndication master down to
   854x480 at ~760kbps (12MB). That is deliberate, not lazy: the card renders
   about 400px wide, the source is grainy broadcast footage that eats bitrate,
   and this repo's next-largest committed asset is 6MB. Denoise before scaling
   is what made it fit -- a straight CRF encode of the master lands at 38MB.

   preload="none" matters. 12MB is not something to pull down for every visitor
   who opens the gallery and never presses play; the poster carries the card
   until someone actually wants the video. */
const FOX29_STORY_URL = "https://www.fox29.com/video/fmc-cmmdd9u4uy7hrowl";

function FoxNewsCard() {
  return (
    <div
      style={{
        border: "2px solid var(--c-ink)",
        borderRadius: 18,
        overflow: "hidden",
        background: "var(--c-ink)",
        color: "var(--c-cream)",
        boxShadow: "var(--shadow-lift)",
      }}
    >
      <video
        controls
        preload="none"
        playsInline
        // There is no play BUTTON here -- the native control bar is the
        // control -- so the element's own play event is the only hook. It
        // fires again after every pause; pprTrackMedia's per-session dedupe
        // is what keeps one viewer from counting as five.
        onPlay={() => {
          if (window.pprTrackMedia) {
            window.pprTrackMedia({
              id: "/img/fox29-camp-showcase-20260811.mp4",
              kind: "video",
              title: "FOX 29 — Summer Camp Showcase at the Dell",
              collection: "news",
            });
          }
        }}
        poster="/img/fox29-camp-showcase-poster.jpg"
        style={{ width: "100%", aspectRatio: "16 / 9", display: "block", background: "var(--c-plum)" }}
      >
        <source src="/img/fox29-camp-showcase-20260811.mp4" type="video/mp4" />
        {/* Reached only by a browser with no MP4 support, which in practice
            means something very old -- send it to the station's own page
            rather than leaving an empty box. */}
        <a href={FOX29_STORY_URL} target="_blank" rel="noopener noreferrer" style={{ color: "var(--c-accent)" }}>
          Watch the segment on FOX 29
        </a>
      </video>
      <div style={{ padding: "18px 20px 20px" }}>
        <div className="eyebrow" style={{ color: "var(--c-accent)" }}>Summer camp showcase in the news</div>
        <div className="display" style={{ fontSize: 21, marginTop: 8, lineHeight: 1.15, color: "var(--c-cream)" }}>
          Philly youth take the Dell Music Center stage.
        </div>
        <p style={{ fontSize: 13.5, color: "rgba(251,243,226,0.72)", marginTop: 8, lineHeight: 1.5 }}>
          FOX 29 covered the end-of-season showcase closing out Parks &amp;
          Recreation&rsquo;s six-week performing arts camps.
        </p>
        <a
          href={FOX29_STORY_URL}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: "inline-block",
            fontFamily: "var(--f-mono)", fontSize: 11, letterSpacing: "0.1em",
            textTransform: "uppercase", color: "var(--c-accent)", marginTop: 14,
            fontWeight: 700, textDecoration: "underline",
          }}
        >
          Courtesy of FOX 29 &#8599;
        </a>
      </div>
    </div>
  );
}

function FlickrAlbumCard() {
  return (
    <div style={{
      marginTop: 40,
      padding: 24,
      border: "2px solid var(--c-ink)",
      borderRadius: 18,
      background: "var(--c-paper)",
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      gap: 14,
      flexWrap: "wrap",
    }}>
      <div>
        <div className="eyebrow">More photos</div>
        <div className="display" style={{ fontSize: 22, marginTop: 4 }}>
          City Wide Dance Festival &mdash; 4/25/2024
        </div>
        <div style={{ fontSize: 13, color: "var(--c-ink-soft)", marginTop: 4 }}>
          Full album from the Citywide Dance Festival at Venice Island PAC,
          hosted on Flickr.
        </div>
      </div>
      <a
        href="https://www.flickr.com/photos/77365596@N05/albums/72177720316587384/"
        target="_blank"
        rel="noopener"
        className="btn small ochre"
      >
        Open Flickr album ↗
      </a>
    </div>
  );
}

function PhotographerCredit() {
  return (
    <div style={{
      marginTop: 40,
      padding: 28,
      border: "2px solid var(--c-ink)",
      borderRadius: 18,
      background: "var(--c-ink)",
      color: "var(--c-cream)",
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      gap: 18,
      flexWrap: "wrap",
    }}>
      <div style={{ flex: "1 1 320px", minWidth: 0 }}>
        <div className="eyebrow" style={{ color: "var(--c-accent)" }}>Photography by</div>
        <img
          src="/img/renzolution-logo.png"
          alt="Renzolution Photography"
          style={{ display: "block", maxWidth: 280, width: "100%", height: "auto", marginTop: 12 }}
          loading="lazy"
        />
        <p style={{ fontSize: 14, color: "rgba(251,243,226,0.78)", marginTop: 14, lineHeight: 1.55, maxWidth: 520 }}>
          Capturing PPR's new work this summer. For photography inquiries, follow
          along on Instagram or reach out by email.
        </p>
      </div>
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
        <a
          href="https://www.instagram.com/renzolutionnn"
          target="_blank"
          rel="noopener"
          className="btn small ochre"
        >
          @renzolutionnn ↗
        </a>
        <a
          href="mailto:renzolutionphotography@gmail.com"
          className="btn small ghost"
          style={{ color: "var(--c-cream)", borderColor: "var(--c-cream)" }}
        >
          Email →
        </a>
      </div>
    </div>
  );
}

Object.assign(window, { GalleryPage, PhotographerCredit, FlickrAlbumCard, WonderlandCard, FirePlayCard, FoxNewsCard, CampPerformanceVideo });
